import { CONNECTOR_MAP_TO_PATTERN, WORKFLOW_HOOK, WORKFLOW_HOOK_KEY_PATTERN, WORKFLOW_HOOK_VARIABLE_PREFIX, type WorkflowTrigger } from "@sagot/constants";
import { createContext } from "react";
import { CREATE_TICKET_VARIABLES } from "../service/service-constants";
import { isConnectorCall, type AnyHttpRequestStep, type Definition, type HookExampleValue } from "../types";

export type ExampleProblem = "json" | "object" | "count" | "key" | "duplicate" | "value";
export type ParsedExample = { ok: true; body: Record<string, HookExampleValue> | undefined } | { ok: false; problem: ExampleProblem; key?: string };

function isScalar(value: unknown): value is HookExampleValue {
  if (value === null || typeof value === "boolean" || typeof value === "number") return true;
  return typeof value === "string" && value.length <= WORKFLOW_HOOK.valueMax;
}

/**
 * The example body of a "Wait for webhook" step as the author typed it: a flat JSON object of scalars with variable-safe
 * keys (what the server accepts, so a body that passes here is never refused on save). Empty text means "no example".
 */
export function parseExampleBody(text: string): ParsedExample {
  if (text.trim() === "") return { ok: true, body: undefined };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, problem: "json" };
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return { ok: false, problem: "object" };
  const entries = Object.entries(parsed);
  if (entries.length > WORKFLOW_HOOK.exampleKeysMax) return { ok: false, problem: "count" };
  const seen = new Set<string>();
  const body: Record<string, HookExampleValue> = {};
  for (const [key, value] of entries) {
    if (!WORKFLOW_HOOK_KEY_PATTERN.test(key)) return { ok: false, problem: "key", key };
    if (seen.has(key.toLowerCase())) return { ok: false, problem: "duplicate", key };
    seen.add(key.toLowerCase());
    if (!isScalar(value)) return { ok: false, problem: "value", key };
    body[key] = value;
  }
  return { ok: true, body: entries.length > 0 ? body : undefined };
}

/** The example as editable text. */
export const exampleText = (body: Record<string, HookExampleValue> | undefined): string => (body ? JSON.stringify(body, null, 2) : "");

/** `webhook.<key>` for each key of the example: the variables later steps can use. */
export const hookVariables = (body: Record<string, HookExampleValue> | undefined): string[] => Object.keys(body ?? {}).map((key) => `${WORKFLOW_HOOK_VARIABLE_PREFIX}${key}`);

/** The body fields every "Wait for webhook" step of the workflow expects (from their examples): conditions can compare `webhook.<key>`. */
export function hookKeysOf(definition: Definition): string[] {
  const keys = new Set<string>();
  for (const path of Object.values(definition.paths)) {
    for (const step of path.steps) {
      if (step.type === "wait_for_webhook") for (const key of Object.keys(step.example_body ?? {})) keys.add(key);
    }
  }
  return [...keys].sort();
}

/** The run variables a connector step fills: the targets of its map (inline form) or of its outputs (operation form). */
function mappedNamesOf(step: AnyHttpRequestStep): string[] {
  const targets = isConnectorCall(step) ? (step.outputs ?? []).map((output) => output.to) : (step.map ?? []).map((row) => row.to);
  return targets.filter((name) => CONNECTOR_MAP_TO_PATTERN.test(name));
}

/**
 * The run variables the workflow fills (`var.<name>`): what a Collect data step saves to a variable and what a Data
 * connector step maps from its answer. A condition can compare them.
 */
export function runVariablesOf(definition: Definition): string[] {
  const names = new Set<string>();
  for (const path of Object.values(definition.paths)) {
    for (const step of path.steps) {
      if (step.type === "collect_data" && CONNECTOR_MAP_TO_PATTERN.test(step.save_to)) names.add(step.save_to);
      if (step.type === "http_request") for (const name of mappedNamesOf(step)) names.add(name);
      // Create ticket names the new ticket for the steps after it (Link ticket reads it).
      if (step.type === "create_ticket") for (const name of CREATE_TICKET_VARIABLES) names.add(name);
    }
  }
  return [...names].sort();
}

/** The variables of `runVariablesOf` for the condition editors below the builder. */
export const RunVariablesContext = createContext<readonly string[]>([]);

/** The keys of `hookKeysOf` for the condition editors below the builder (they are far from the definition). */
export const HookKeysContext = createContext<readonly string[]>([]);

/** The workflow's trigger for the condition editors below the builder: it decides which fields they can offer. */
export const TriggerContext = createContext<WorkflowTrigger | undefined>(undefined);
